import type { MessageType } from '@flowx/angular-ui-toolkit'
import {
  AttachmentType,
  type ComposerFile,
  ComposerFileMark,
  type FileChipView,
  type FileRefusal,
  FileRefusalReason,
  type FoldSummary,
  type FoldedFiles,
} from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import type { HarnessAttachmentsPolicy } from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import { COMPOSER_FILE_TEXTS } from '@flowx/designer/modules/ai-builder/utils/composer-files.dictionary'
import {
  MIME_DISPLAY,
  fileSize,
  mimeLabel,
} from '@flowx/designer/modules/ai-builder/utils/file-pick.util'

/** One glyph per type, carrying the builder palette's tint. */
export const FILE_TYPE_GLYPH: Readonly<Record<AttachmentType, string>> = {
  [AttachmentType.Pdf]: 'ph-file-pdf text-builder-warn',
  [AttachmentType.Doc]: 'ph-file-doc text-builder-accent',
  [AttachmentType.Image]: 'ph-file-image text-builder-violet',
  [AttachmentType.Bpmn]: 'ph-flow-arrow text-builder-accent',
  [AttachmentType.Other]: 'ph-file text-builder-muted',
}

/** Each type's glyph untinted, for a chip that greys it with its text. */
export const FILE_TYPE_ICON: Readonly<Record<AttachmentType, string>> = {
  [AttachmentType.Pdf]: 'ph-file-pdf',
  [AttachmentType.Doc]: 'ph-file-doc',
  [AttachmentType.Image]: 'ph-file-image',
  [AttachmentType.Bpmn]: 'ph-flow-arrow',
  [AttachmentType.Other]: 'ph-file',
}

/** Lists longer than this fold to their first `FOLD_SHOWN` chips and a "+N more" chip. */
export const FOLD_AFTER = 4
export const FOLD_SHOWN = 3

const DUPLICATE_REASONS: ReadonlySet<FileRefusalReason> = new Set([
  FileRefusalReason.AlreadyAttached,
  FileRefusalReason.AlreadyInConversation,
])

/** The notice's heading: "This file wasn’t attached" or "{n} files weren’t attached". */
export function refusalHeading(count: number): string {
  return count === 1 ? 'This file wasn’t attached' : `${count} files weren’t attached`
}

/** The supported-types footer, from the policy: "Attach PDF, DOCX, PNG or JPG." */
function supportedTypesLine(policy: HarnessAttachmentsPolicy): string {
  const labels = [
    ...new Set(Object.keys(policy.types).flatMap((mime) => MIME_DISPLAY[mime]?.label ?? [])),
  ]

  return `Attach ${listed(labels)}.`
}

function listed(words: readonly string[]): string {
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} or ${words.at(-1)}` : words.join('')
}

/** Information when every refused file is a duplicate; otherwise the danger tint the prototype
 * paints a refusal in. */
export function refusalMessageType(refusals: readonly FileRefusal[]): MessageType {
  return refusals.every((refusal) => DUPLICATE_REASONS.has(refusal.reason)) ? 'info' : 'error'
}

/** The supported-types footer, only when a type was a reason. */
export function refusalFooter(
  refusals: readonly FileRefusal[],
  policy: HarnessAttachmentsPolicy
): string | null {
  return refusals.some((refusal) => refusal.reason === FileRefusalReason.UnsupportedType)
    ? supportedTypesLine(policy)
    : null
}

/** The first chips and the rest, when the list is longer than `FOLD_AFTER` and not opened. */
export function foldFiles(files: readonly ComposerFile[], open: boolean): FoldedFiles {
  return files.length > FOLD_AFTER && !open
    ? { shown: files.slice(0, FOLD_SHOWN), hidden: files.slice(FOLD_SHOWN) }
    : { shown: files, hidden: [] }
}

/** The fold chip's words about the hidden chips: "+{n} more", then " · {k} uploading", and apart,
 * so it can be red, " · {k} failed". */
export function foldSummary({ shown, hidden }: FoldedFiles): FoldSummary {
  const count = (mark: ComposerFileMark): number =>
    hidden.filter((file) => chipMark(file) === mark).length
  const uploading = count(ComposerFileMark.Uploading)
  const failed = count(ComposerFileMark.Failed)

  return {
    label: `+${hidden.length} more${uploading ? ` · ${uploading} uploading` : ''}`,
    failed: failed ? ` · ${failed} failed` : null,
    title: `Show all ${shown.length + hidden.length} files`,
  }
}

/** Which mark a chip draws before its name. */
export function chipMark(file: ComposerFile): ComposerFileMark {
  if (file.error) {
    return ComposerFileMark.Failed
  }

  return file.tempFileId ? ComposerFileMark.Attached : ComposerFileMark.Uploading
}

/** What the chip says beside the name: how far the upload has got, the size once attached, or
 * the failure. */
export function chipMeta(file: ComposerFile): string {
  const meta: Readonly<Record<ComposerFileMark, () => string>> = {
    [ComposerFileMark.Uploading]: () => `${file.progress ?? 0}%`,
    [ComposerFileMark.Attached]: () => fileSize(file.size),
    [ComposerFileMark.Failed]: () => file.error ?? COMPOSER_FILE_TEXTS.unreadable,
  }

  return meta[chipMark(file)]()
}

/** The chip's tooltip: name, type, size and its state. */
export function chipTooltip(file: ComposerFile): string {
  const state: Readonly<Record<ComposerFileMark, () => string>> = {
    [ComposerFileMark.Uploading]: () => ` · uploading, ${file.progress ?? 0}%`,
    [ComposerFileMark.Attached]: () => '',
    [ComposerFileMark.Failed]: () => ` · ${file.error ?? COMPOSER_FILE_TEXTS.unreadable}`,
  }

  return `${file.name} · ${mimeLabel(file.mime)} · ${fileSize(file.size)}${state[chipMark(file)]()}`
}

/** The × button's label: "Cancel the upload" while the bytes go up, else "Remove". */
export function removeLabel(file: ComposerFile): string {
  return chipMark(file) === ComposerFileMark.Uploading
    ? COMPOSER_FILE_TEXTS.cancelUpload
    : COMPOSER_FILE_TEXTS.remove
}

/** The glyph a composer chip draws in each mark: null where the type's own glyph stands, or a
 * spinner does. */
const COMPOSER_MARK_GLYPH: Readonly<Record<ComposerFileMark, string | null>> = {
  [ComposerFileMark.Uploading]: null,
  [ComposerFileMark.Attached]: null,
  [ComposerFileMark.Failed]: 'ph-warning-circle',
}

/** A composer file as its chip draws it. */
export function composerChipView(file: ComposerFile): FileChipView {
  const mark = chipMark(file)

  return {
    glyph: COMPOSER_MARK_GLYPH[mark] ?? FILE_TYPE_GLYPH[file.type],
    loading: mark === ComposerFileMark.Uploading,
    progress: file.progress ?? 0,
    name: file.name,
    tooltip: chipTooltip(file),
    meta: chipMeta(file),
    failed: mark === ComposerFileMark.Failed,
    retryable: mark === ComposerFileMark.Failed && file.recoverable,
    removed: false,
    openable: false,
  }
}

/** Whether every file on the composer is attached — the only state the composer sends in. */
export function allFilesReady(files: readonly ComposerFile[]): boolean {
  return files.every((file) => chipMark(file) === ComposerFileMark.Attached)
}

/** Why the files block Send, or null when they do not. */
export function sendBlockReasonFor(files: readonly ComposerFile[]): string | null {
  const marks = files.map(chipMark)
  const uploading = marks.filter((mark) => mark === ComposerFileMark.Uploading).length

  if (marks.includes(ComposerFileMark.Failed)) {
    return COMPOSER_FILE_TEXTS.removeFailed
  }

  if (!uploading) {
    return null
  }

  return uploading === 1 ? COMPOSER_FILE_TEXTS.waitForUpload : COMPOSER_FILE_TEXTS.waitForUploads
}
